import type {IconName} from '../icons/paths';
import type {Kind} from '../types';

// The kinds (DESIGN.md §4: one icon and one colour family each), grouped as Concepts §2 groups
// them, plus the one-tap details each kind offers and the ordering by what this gardener uses
// most. The server list lives in src/posts/kinds.ts; test/kinds.test.ts keeps them in step.

export type Family = 'grow' | 'warm' | 'care' | 'alert' | 'plain' | 'seed';

export interface KindInfo {
    kind: Kind;
    label: string;
    icon: IconName;
    family: Family;
}

export const KINDS: KindInfo[] = [
    {kind: 'sowed', label: 'Sowed', icon: 'sowed', family: 'grow'},
    {kind: 'planted', label: 'Planted out', icon: 'planted', family: 'grow'},
    {kind: 'potted', label: 'Potted on', icon: 'potted', family: 'grow'},
    {kind: 'watered', label: 'Watered', icon: 'watered', family: 'care'},
    {kind: 'fed', label: 'Fed / treated', icon: 'fed', family: 'care'},
    {kind: 'pruned', label: 'Pruned', icon: 'pruned', family: 'care'},
    {kind: 'chore', label: 'Chore', icon: 'chore', family: 'care'},
    {kind: 'established', label: 'Established', icon: 'tree', family: 'grow'},
    {kind: 'progress', label: 'Progress', icon: 'progress', family: 'grow'},
    {kind: 'problem', label: 'Problem', icon: 'problem', family: 'alert'},
    {kind: 'weather', label: 'Weather', icon: 'weather', family: 'plain'},
    {kind: 'measured', label: 'Measured', icon: 'measured', family: 'plain'},
    {kind: 'note', label: 'Note', icon: 'note', family: 'plain'},
    {kind: 'harvest', label: 'Harvest', icon: 'harvest', family: 'warm'},
    {kind: 'gave', label: 'Gave away', icon: 'gave', family: 'warm'},
    {kind: 'made', label: 'Made', icon: 'made', family: 'warm'},
    {kind: 'reminder', label: 'To do', icon: 'reminder', family: 'warm'}
];

/** What a tell can be: every kind but the to-do. */
export const TELL_KINDS: Kind[] = KINDS.map(k => k.kind).filter(k => k !== 'reminder');

export const kindInfo = (kind: Kind): KindInfo => KINDS.find(k => k.kind === kind) ?? KINDS.find(k => k.kind === 'note')!;

export const KIND_GROUPS: {label: string; kinds: Kind[]}[] = [
    {label: 'Doing', kinds: ['sowed', 'planted', 'potted', 'watered', 'fed', 'pruned', 'chore', 'established']},
    {label: 'Watching', kinds: ['progress', 'problem', 'weather', 'measured', 'note']},
    {label: 'Getting', kinds: ['harvest', 'gave', 'made']},
    {label: 'Planning', kinds: ['reminder']}
];

/** The picker's groups: a to-do is made from the To do tab or "Remind me", not picked as a tell. */
export const TELL_GROUPS = KIND_GROUPS.filter(g => !g.kinds.includes('reminder'));

/** The six a new gardener sees first, in the design's order. */
export const DEFAULT_KINDS: Kind[] = ['sowed', 'planted', 'progress', 'harvest', 'problem', 'note'];

export const LATER_KINDS: {label: string; icon: IconName; phase: number}[] = [{label: 'Seed', icon: 'seed', phase: 5}];

/** One-tap details per kind: a word that says which chore, which treatment, which trouble. */
export const KIND_DETAILS: Partial<Record<Kind, string[]>> = {
    sowed: ['indoors', 'direct', 'in trays'],
    planted: ['from pots', 'bare root', 'cloves', 'corms', 'transplanted'],
    potted: ['cuttings', 'potted on', 'divided'],
    watered: ['by hand', 'hose', 'irrigation'],
    fed: ['fertiliser', 'compost', 'manure', 'lime', 'liquid feed', 'seaweed', 'fish scraps', 'wood ash', 'spray', 'copper', 'slug pellets'],
    pruned: ['tidy', 'hard', 'deadheaded'],
    established: ['tree', 'shrub', 'perennial', 'hedge', 'climber'],
    chore: ['cleared', 'weeded', 'mulched', 'dug over', 'composted', 'tidied', 'staked', 'netted', 'covers on', 'covers off', 'windbreak', 'trellis', 'built'],
    problem: ['pest', 'disease', 'damage', 'weeds', 'frost damage', 'wind damage', 'bolted', 'drought'],
    weather: ['frost', 'rain', 'hail', 'wind', 'heat', 'dry spell', 'first frost', 'last frost'],
    measured: ['pH', 'rain', 'temperature', 'height', 'weight', 'girth'],
    harvest: ['first', 'main', 'last', 'seed']
};

/**
 * Kinds by this gardener's use: most-used first (ties by list order), then the design's default
 * six that are still unused, then everything else in list order. A new gardener sees the six.
 */
export function orderByUsage(counts: Partial<Record<Kind, number>>): Kind[] {
    const all = TELL_KINDS;
    const used = all.filter(k => (counts[k] ?? 0) > 0).sort((a, b) => (counts[b] ?? 0) - (counts[a] ?? 0) || all.indexOf(a) - all.indexOf(b));
    const unused = (k: Kind) => !used.includes(k);
    return [...used, ...DEFAULT_KINDS.filter(unused), ...all.filter(k => unused(k) && !DEFAULT_KINDS.includes(k))];
}

/** Tailwind classes for a family's soft tile and ink (mapped from the --kind-* tokens). */
export const FAMILY_BG: Record<Family, string> = {
    grow: 'bg-kind-grow-bg',
    warm: 'bg-kind-warm-bg',
    care: 'bg-kind-care-bg',
    alert: 'bg-kind-alert-bg',
    plain: 'bg-kind-plain-bg',
    seed: 'bg-kind-seed-bg'
};

export const FAMILY_FG: Record<Family, string> = {
    grow: 'text-kind-grow-fg',
    warm: 'text-kind-warm-fg',
    care: 'text-kind-care-fg',
    alert: 'text-kind-alert-fg',
    plain: 'text-kind-plain-fg',
    seed: 'text-kind-seed-fg'
};

export const familyClasses = (family: Family): string => `${FAMILY_BG[family]} ${FAMILY_FG[family]}`;
